import {useState, useRef, useEffect, useMemo} from "react";
import {parse, stringify} from "flatted";
import {useDebounce} from "use-debounce";

export function usePersistedState<T>(
  storageKey: string | undefined,
  defaultValue?: T,
  updateDelay = 500
) {
  const [rawValue, setRawValue] = useState<T>();
  const [value, setValue] = useState<string>();
  const [debouncedValue] = useDebounce(value, updateDelay);
  const defaultRef = useRef(defaultValue);

  useEffect(() => {
    if (!storageKey) {
      return;
    }

    const item = localStorage.getItem(storageKey);
    if (item) {
      setValue(item);
      setRawValue(parse(item));
    } else {
      setValue(stringify(defaultRef.current));
      setRawValue(defaultRef.current);
    }
  }, [storageKey]);

  useEffect(() => {
    setValue((previousValue) => {
      if (stringify(rawValue) !== previousValue) {
        return stringify(rawValue);
      }

      return previousValue;
    });
  }, [rawValue]);

  useEffect(() => {
    if (debouncedValue && storageKey) {
      localStorage.setItem(storageKey, debouncedValue);
    }
  }, [debouncedValue, storageKey]);

  return useMemo(() => [rawValue, setRawValue] as const, [rawValue]);
}
